import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ActivityIcon, ArrowRightIcon, BellRingIcon, CheckCircle2Icon, LockIcon, MailCheckIcon, RadioIcon } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { TextInput } from '../../components/ui/TextInput';
import { EmailPreview } from '../../components/EmailPreview';
import { ExpiryRunway } from '../../components/ExpiryRunway';
import { GridBackdrop } from '../../components/GridBackdrop';
import { LiveTicker } from '../../components/LiveTicker';
import { Sparkline } from '../../components/Sparkline';
import { WatchtowerMark } from '../../components/WatchtowerMark';
const uptimeBoard = [{
  domain: 'acme.com',
  availability: '100.00%',
  up: true,
  values: [212, 196, 231, 188, 204, 176, 219, 198, 241, 187, 208, 223, 195, 179, 214, 201]
}, {
  domain: 'shop.acme.com',
  availability: '99.97%',
  up: true,
  values: [265, 248, 301, 272, 259, 288, 244, 312, 267, 251, 279, 294, 261, 273, 256, 268]
}, {
  domain: 'api.acme.com',
  availability: '96.40%',
  up: false,
  values: [128, 141, 119, 134, 126, 147, 131, 122, 0, 0, 0, 0, 0, 0, 0, 0]
}];
const dayStages = [{
  time: '08:00',
  icon: LockIcon,
  title: 'The daily sweep',
  body: 'Every certificate you watch is read fresh — issuer, chain, serial, expiry date — and the day count is recalculated.'
}, {
  time: 'every minute',
  icon: RadioIcon,
  title: 'The ping cycle',
  body: 'Uptime monitors run continuously on their own interval. A site is only called down after the failures you allow for.'
}, {
  time: '08:04',
  icon: MailCheckIcon,
  title: 'The email',
  body: 'Anything that crossed a threshold becomes an email to your verified recipients, and a permanent record you can point at.'
}];
export function Landing() {
  const [domain, setDomain] = useState('');
  const [footerDomain, setFooterDomain] = useState('');
  const navigate = useNavigate();
  return <>
      <section className="relative overflow-hidden bg-shell" data-id="element-1563" data-mp="745aj9uuwnvd">
        <GridBackdrop data-id="element-1564" data-mp="27ezj0xwsigt" />
        <WatchtowerMark beam={false} className="pointer-events-none absolute -left-20 -top-12 h-[440px] w-[440px] text-brand-bright/[0.045]" data-id="element-1565" data-mp="25vi370io1dg" />

        <div className="relative mx-auto grid w-full max-w-page items-center gap-14 px-5 pb-16 pt-16 sm:px-8 lg:grid-cols-[0.94fr_1.06fr] lg:pb-24 lg:pt-24" data-id="element-1566" data-mp="bw6lmsgag8jt">
          <div data-id="element-1567" data-mp="3ke32ol9eevju">
            <p className="flex items-center gap-2.5 font-mono text-xs uppercase tracking-[0.16em] text-brand-bright" data-id="element-1568" data-mp="3fd6qlkew833c">
              <span className="h-px w-8 bg-brand-bright/60" aria-hidden="true" data-id="element-1569" data-mp="3uaz4k84skjff" />
              TLS · HTTPS · uptime
            </p>
            <h1 className="mt-5 text-[2.9rem] font-semibold leading-[1.02] tracking-[-0.02em] text-white sm:text-[3.8rem] lg:text-[4.1rem]" data-id="element-1570" data-mp="3p9rt9z3fx240">
              Nine days left.
              <span className="block text-white/45" data-id="element-1571" data-mp="14lx2h99b1ojm">
                Nobody was going to tell you.
              </span>
            </h1>
            <p className="mt-7 max-w-lg text-lg leading-relaxed text-white/65" data-id="element-1572" data-mp="1c3bpdjqw5n2h">
              Watchtower reads your certificates every morning and pings your
              sites all day. You find out from an email, not from a customer.
            </p>

            <form className="mt-9 flex max-w-md flex-col gap-2.5 sm:flex-row" onSubmit={event => {
            event.preventDefault();
            navigate('/signup', {
              state: {
                domain
              }
            });
          }} data-id="element-1573" data-mp="1750h3dg8vkmd">
              <label htmlFor="hero-domain" className="sr-only" data-id="element-1574" data-mp="1zwbpdb0a43ft">
                Your domain
              </label>
              <TextInput id="hero-domain" placeholder="yourdomain.com" value={domain} onChange={event => setDomain(event.target.value)} className="h-11 border-transparent font-mono sm:flex-1" data-id="element-1575" data-mp="1m0ztv1eau8xu" />
              <Button type="submit" variant="bright" size="lg" className="group" data-id="element-1576" data-mp="2imjpdk8omdrs">
                Check my certificate
                <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-snap group-hover:translate-x-0.5" aria-hidden="true" data-id="element-1577" data-mp="pxkco1xiyi3w" />
              </Button>
            </form>

            <p className="mt-4 text-[13px] text-white/50" data-id="element-1578" data-mp="gxxmkca8wyq5">
              From $2 a month for two services · no card to see your own
              certificate
            </p>
          </div>

          <div className="relative lg:pb-10" data-id="element-1579" data-mp="18otnqfmy1exl">
            <ExpiryRunway data-id="element-1580" data-mp="18pv8xcxobht1" />

            <div className="absolute -bottom-2 -left-4 hidden items-center gap-2.5 rounded-card border border-shell-line bg-shell px-4 py-3 shadow-pop lg:flex" data-id="element-1581" data-mp="13wtqiuy9d04p">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-bright/15" data-id="element-1582" data-mp="ztdzce4yjrze">
                <MailCheckIcon className="h-3.5 w-3.5 text-brand-bright" aria-hidden="true" data-id="element-1583" data-mp="1wf7fe51m9brn" />
              </span>
              <span data-id="element-1584" data-mp="3aaz38u73927g">
                <span className="block font-mono text-[11px] text-white" data-id="element-1585" data-mp="3abh4y6cis8mr">
                  shop.acme.com · 9d warning
                </span>
                <span className="block font-mono text-[11px] text-white/45" data-id="element-1586" data-mp="3j9t6h3excqtz">
                  emailed 2 recipients · 08:00:03
                </span>
              </span>
            </div>
          </div>
        </div>

        <LiveTicker data-id="element-1587" data-mp="3t778hy03pd2r" />
      </section>

      <section className="border-b border-line bg-surface" data-id="element-1588" data-mp="3t65w9q7cmkjg">
        <div className="mx-auto w-full max-w-page px-5 py-16 sm:px-8 lg:py-20" data-id="element-1589" data-mp="20o34yd1afbu">
          <div className="max-w-2xl" data-id="element-1590" data-mp="6sfisdunj0n7">
            <h2 className="text-[1.9rem] font-semibold leading-tight tracking-tight text-ink sm:text-[2.4rem]" data-id="element-1591" data-mp="6th73k0ohjmv">
              Two checks, mixed however your sites need
            </h2>
            <p className="mt-3 text-base leading-relaxed text-ink-muted" data-id="element-1592" data-mp="fuowfcl7va4t">
              Every domain you add has both checks as independent switches. Turn
              on only what that site warrants.
            </p>
          </div>

          <div className="mt-10 overflow-hidden rounded-card border border-line bg-surface shadow-card" data-id="element-1593" data-mp="2gt8up07x2m1r">
            <div className="grid divide-y divide-line lg:grid-cols-2 lg:divide-x lg:divide-y-0" data-id="element-1594" data-mp="2qleli6mfmxfd">
              <article className="p-6 sm:p-8" data-id="element-1595" data-mp="2qn7rdjp81dmb">
                <h3 className="flex items-center gap-2.5 text-base font-semibold text-ink" data-id="element-1596" data-mp="2ltn1yg1p1ryq">
                  <LockIcon className="h-4 w-4 text-brand" aria-hidden="true" data-id="element-1597" data-mp="2zl1p8z2mwfbl" />
                  Certificate expiry
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-ink-muted" data-id="element-1598" data-mp="2vmkfs8vqqsr0">
                  Read once a day and counted down. You choose the warning
                  window and the thresholds that trigger an email, so you hear
                  about it repeatedly rather than once.
                </p>

                <div className="mt-6 rounded-card border border-warn-border bg-warn-soft p-5" data-id="element-1599" data-mp="1f8c81kfmqnzf">
                  <div className="flex items-end justify-between gap-4" data-id="element-1600" data-mp="1a99cqrpgbw4n">
                    <div data-id="element-1601" data-mp="3lv0c61c2q57o">
                      <p className="font-mono text-[2.6rem] font-medium leading-none text-warn-ink" data-id="element-1602" data-mp="3lwl3zj707a1u">
                        9
                      </p>
                      <p className="mt-2 font-mono text-xs text-warn-ink/80" data-id="element-1603" data-mp="3c3nkpau6th26">
                        days · shop.acme.com
                      </p>
                    </div>
                    <div className="flex gap-1.5" data-id="element-1604" data-mp="334a7c72y08c6">
                      {['30d', '14d', '7d', '1d'].map((threshold, index) => <span key={threshold} className={`rounded border px-1.5 py-1 font-mono text-[11px] font-medium ${index > 1 ? 'border-warn-ink bg-warn-ink text-warn-soft' : 'border-warn-border text-warn-ink/70'}`} data-id="element-1605" data-mp="383v7ulxrgor0" data-mpi={index}>
                          {threshold}
                        </span>)}
                    </div>
                  </div>
                </div>
                <p className="mt-3 text-xs text-ink-soft" data-id="element-1606" data-mp="3b9yzwo3term0">
                  Filled thresholds have already been sent. Broken chains and
                  failed handshakes raise the same alarm.
                </p>
              </article>

              <article className="p-6 sm:p-8" data-id="element-1607" data-mp="31k5uy5y6haqq">
                <h3 className="flex items-center gap-2.5 text-base font-semibold text-ink" data-id="element-1608" data-mp="2wlclqppxc5wd">
                  <ActivityIcon className="h-4 w-4 text-brand" aria-hidden="true" data-id="element-1609" data-mp="2sk935vghrvrg" />
                  Uptime
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-ink-muted" data-id="element-1610" data-mp="2nmr1mq4t0ith">
                  A request as often as every minute, with the method, expected
                  status and timeout you set. Down only counts after the
                  consecutive failures you allow.
                </p>

                <ul className="mt-6 divide-y divide-line overflow-hidden rounded-card border border-line" data-id="element-1611" data-mp="odz98s0fmykc">
                  {uptimeBoard.map(row => <li key={row.domain} className="flex items-center gap-3 px-4 py-3.5 transition-colors duration-150 ease-snap hover:bg-canvas" data-id="element-1612" data-mp="tbraicmyvx17">
                      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${row.up ? 'bg-healthy' : 'bg-critical'}`} aria-hidden="true" data-id="element-1613" data-mp="1323uo3qvdo6t" />
                      <span className="min-w-0 flex-1 truncate font-mono text-[13px] text-ink" data-id="element-1614" data-mp="fd1el0bvw3ef">
                        {row.domain}
                      </span>
                      <Sparkline values={row.values} count={16} label={`Response times for ${row.domain}`} data-id="element-1615" data-mp="ka1bakmuftsv" />
                      <span className={`w-[66px] shrink-0 text-right font-mono text-[13px] ${row.up ? 'text-ink-muted' : 'text-critical-ink'}`} data-id="element-1616" data-mp="1vtdp23e5r14p">
                        {row.availability}
                      </span>
                    </li>)}
                </ul>
                <p className="mt-3 text-xs text-ink-soft" data-id="element-1617" data-mp="37nq6dc0wmwtx">
                  Red bars are failed checks. api.acme.com has been down 52
                  minutes and its alert went out at the third failure.
                </p>
              </article>
            </div>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 bg-shell px-6 py-4 sm:px-8" data-id="element-1618" data-mp="2juzzhh7uz3qi">
              <p className="font-mono text-[13px] font-medium text-brand-bright" data-id="element-1619" data-mp="2ovxddhlu5bm7">
                1 check on 1 domain = 1 service
              </p>
              <p className="text-[13px] text-white/60" data-id="element-1620" data-mp="44wvx83fao3z">
                So four services is two sites covered completely, or four sites
                watched for expiry alone.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-line bg-mist" data-id="element-1621" data-mp="1jp5z4omsguyf">
        <div className="mx-auto w-full max-w-page px-5 py-16 sm:px-8 lg:py-20" data-id="element-1622" data-mp="1ewuxnhceh13r">
          <div className="max-w-2xl" data-id="element-1623" data-mp="1eurzub50a3oh">
            <h2 className="text-[1.9rem] font-semibold leading-tight tracking-tight text-ink sm:text-[2.4rem]" data-id="element-1624" data-mp="19ytaolbushn0">
              A day of monitoring, in three moments
            </h2>
            <p className="mt-3 text-base leading-relaxed text-ink-muted" data-id="element-1625" data-mp="10wtwe0s83rnr">
              Nothing to open, nothing to remember. The schedule runs whether
              you think about it or not.
            </p>
          </div>

          <ol className="relative mt-14 grid gap-10 md:grid-cols-3 md:gap-8" data-id="element-1626" data-mp="r08ptb0k28ht">
            <span className="absolute left-2 right-2 top-[7px] hidden h-px bg-line-strong md:block" aria-hidden="true" data-id="element-1627" data-mp="2qcta7v6se59q" />
            {dayStages.map(stage => <li key={stage.time} className="relative" data-id="element-1628" data-mp="jnran4br39te">
                <span className="relative z-10 block h-3.5 w-3.5 rounded-full border-[3px] border-brand bg-mist" aria-hidden="true" data-id="element-1629" data-mp="jm7tzcm6mwr3" />
                <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.12em] text-brand" data-id="element-1630" data-mp="9ro9a0oq3f7i">
                  {stage.time}
                </p>
                <h3 className="mt-3 flex items-center gap-2 text-lg font-semibold tracking-tight text-ink" data-id="element-1631" data-mp="12e0q3fwjsy31">
                  <stage.icon className="h-4 w-4 text-ink-soft" aria-hidden="true" data-id="element-1632" data-mp="17dvfff0odflz" />
                  {stage.title}
                </h3>
                <p className="mt-2 max-w-sm text-sm leading-relaxed text-ink-muted" data-id="element-1633" data-mp="xeo2uu1rlozx">
                  {stage.body}
                </p>
              </li>)}
          </ol>
        </div>
      </section>

      <section className="border-b border-line bg-surface" data-id="element-1634" data-mp="3i6psn09t6kw6">
        <div className="mx-auto grid w-full max-w-page items-center gap-12 px-5 py-16 sm:px-8 lg:grid-cols-[0.85fr_1.15fr] lg:py-20" data-id="element-1635" data-mp="3am5g99folih2">
          <div data-id="element-1636" data-mp="35m105kioqprd">
            <h2 className="text-[1.9rem] font-semibold leading-tight tracking-tight text-ink sm:text-[2.4rem]" data-id="element-1637" data-mp="35mj4l4fi4aad">
              This email is the product
            </h2>
            <p className="mt-4 text-base leading-relaxed text-ink-muted" data-id="element-1638" data-mp="3fkyw38ifxlke">
              Everything else is somewhere to configure it and somewhere to
              prove it went out. The warning itself carries the domain, the
              issuer, the exact days left, and when you will hear from us next.
            </p>
            <ul className="mt-7 space-y-3.5 border-t border-line pt-6 text-sm" data-id="element-1639" data-mp="31m8xtpjvxfug">
              <li className="flex gap-3" data-id="element-1640" data-mp="2wuh4565fmu03">
                <MailCheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-brand" aria-hidden="true" data-id="element-1641" data-mp="2wsnxvryqebsm" />
                <span className="text-ink" data-id="element-1642" data-mp="246v4ua38mbj1">
                  Sent to every recipient who has confirmed their address
                </span>
              </li>
              <li className="flex gap-3" data-id="element-1643" data-mp="17cvcq0ucomyr">
                <BellRingIcon className="mt-0.5 h-4 w-4 shrink-0 text-brand" aria-hidden="true" data-id="element-1644" data-mp="1bficcnx9jc6i" />
                <span className="text-ink" data-id="element-1645" data-mp="1gda1xbafqm7j">
                  Repeated on each threshold, not once and forgotten
                </span>
              </li>
              <li className="flex gap-3" data-id="element-1646" data-mp="1q44okgrlfnti">
                <ActivityIcon className="mt-0.5 h-4 w-4 shrink-0 text-brand" aria-hidden="true" data-id="element-1647" data-mp="1q7acvtseelo6" />
                <span className="text-ink" data-id="element-1648" data-mp="1z5mdmomzazuc">
                  Delivery recorded, so you can show it was sent
                </span>
              </li>
            </ul>
          </div>

          <div className="relative" data-id="element-1649" data-mp="4sczuknegs6a">
            <EmailPreview kind="expiry" domain="shop.acme.com" days={9} recipients={['priya@acme.com', 'ops@acme.com']} reminderCadence={[7, 3, 1]} data-id="element-1650" data-mp="4t4t3otiydte" />
            <span className="absolute -right-3 -top-3 hidden items-center gap-1.5 rounded-full border border-healthy-border bg-healthy-soft px-2.5 py-1 shadow-card sm:flex" data-id="element-1651" data-mp="2989ao39y0am">
              <CheckCircle2Icon className="h-3 w-3 text-healthy" aria-hidden="true" data-id="element-1652" data-mp="3onjbyw7v4g7h" />
              <span className="font-mono text-[11px] font-medium text-healthy-ink" data-id="element-1653" data-mp="l69f2b4nk4fm">
                delivered 08:00:03
              </span>
            </span>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden border-b border-shell-line bg-shell" data-id="element-1654" data-mp="b83d5tiuiyar">
        <GridBackdrop opacity={0.35} data-id="element-1655" data-mp="b9y35m8z5glp" />
        <div className="relative mx-auto w-full max-w-page px-5 py-16 sm:px-8" data-id="element-1656" data-mp="779ouz1hrhhc">
          <div className="grid items-center gap-10 lg:grid-cols-[1fr_auto]" data-id="element-1657" data-mp="2vy2mo7fjhffh">
            <div data-id="element-1658" data-mp="2vvzm4tgrg3wi">
              <h2 className="text-[1.9rem] font-semibold leading-tight tracking-tight text-white sm:text-[2.3rem]" data-id="element-1659" data-mp="2rwz3pc2733a1">
                Add your first domain in under a minute
              </h2>
              <p className="mt-3 max-w-xl text-base leading-relaxed text-white/60" data-id="element-1660" data-mp="2ycze0sh3sgtp">
                Two services for{' '}
                <span className="font-mono text-white" data-id="element-1661" data-mp="2jgi13wt9mu51">$2</span> a month. Cancel
                whenever the anxiety stops being worth two dollars.
              </p>
            </div>

            <form className="flex w-full flex-col gap-2.5 sm:flex-row lg:w-auto" onSubmit={event => {
            event.preventDefault();
            navigate('/signup', {
              state: {
                domain: footerDomain
              }
            });
          }} data-id="element-1662" data-mp="2anohjn6sd8ld">
              <label htmlFor="cta-domain" className="sr-only" data-id="element-1663" data-mp="20ne2dx16xr3k">
                Your domain
              </label>
              <TextInput id="cta-domain" placeholder="yourdomain.com" value={footerDomain} onChange={event => setFooterDomain(event.target.value)} className="h-11 border-transparent font-mono sm:w-[220px]" data-id="element-1664" data-mp="1roiveiqbocz7" />
              <Button type="submit" variant="bright" size="lg" data-id="element-1665" data-mp="1830jyswfmgur">
                Start monitoring
              </Button>
            </form>
          </div>
        </div>
      </section>
    </>;
}